import { useState, useEffect, useContext } from "react";
import { Row, Col, Tabs, Button, Input, Dropdown, Menu } from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import { EditOutlined, DeleteOutlined, CheckOutlined } from "@ant-design/icons";

import DataTable from "../DataTable";
import { AddProductModal } from "../../components/ModalForm";
import { EditProductModal } from "../../components/ModalForm";

import { AuthContext } from "../../context/AuthContext";

import { getGroupApi } from "../../services/Group/GroupApis";
import { searchProductsApi } from "../../services/Product/ProductApis";

const { TabPane } = Tabs;
const { Search } = Input;

interface IProductProps {
  products: any;
  groupId: string;
  handleAdd: Function;
  handleUpdate: Function;
  handleDelete: Function;
}

const ProductTable: React.FC<IProductProps> = ({
  products,
  groupId,
  handleAdd,
  handleUpdate,
  handleDelete,
}) => {
  const { currentGroup } = useContext(AuthContext);

  const [active, setActive] = useState([]); //All Tab
  const [inactive, setInactive] = useState([]); //Cancelled Tab
  const [product, setProduct] = useState({});
  const [update, setUpdate] = useState(false);
  const [show, setShow] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [filteredActive, setFilteredActive] = useState([]); //Active Tab
  const [filteredInActive, setFilteredInActive] = useState([]); //Active Tab

  const [state, setState] = useState({
    loading: true,
    activeKey: "1",
    pagination: {
      pageSize: 15,
      position: ["bottomRight"],
      total: 0,
      current: 1,
      hideOnSinglePage: true,
    },
    searchItem: "",
  });

  const showModal = () => {
    setShow(!show);
    setUpdate(false);
  };

  const showEditModal = (data: any) => {
    const formData = {
      id: data?.id ?? null,
      ProductName: data?.ProductName ?? "",
      CoverAmount: data?.CoverAmount ?? null,
      SpouseCoverAmount: data?.SpouseCoverAmount ?? null,
      MinAge:
        data?.ProductCategory?.MinAge ?? data?.MinAge ?? null,
      MaxAge:
        data?.ProductCategory?.MaxAge ?? data?.MaxAge ?? null,
      ProductCategoryID:
        data?.ProductCategory?.id ?? data?.ProductCategoryID ?? null,
      PlanCategoryID: data?.PlanCategory?.id ?? data?.PlanCategoryID ?? null,
      // Select expects the option value (id), not { value, label }
      PlanCategory: data?.PlanCategory?.id ?? data?.PlanCategoryID ?? null,
      UnderwriterPremium: data?.UnderwriterPremium ?? null,
      GroupPremium: data?.GroupPremium ?? null,
      PolicyPremium: data?.PolicyPremium ?? null,
      Commission: data?.Commission ?? null,
      CommissionTwo: data?.CommissionTwo ?? null,
      Addons:
        data?.Addons ??
        data?.Addon?.map((item: any) => item?.id) ??
        [],
      FamilyCover: data?.FamilyCover?.map((item: any) => item) ?? [],
    };

    setProduct(formData);
    setShow(!show);
    setUpdate(true);
  };

  const fetchActive = async (params = {}) => {
    try {
      const active = products.filter((item: any) => item.StatusID === 1);

      if (filtered.length > 0) {
        const activeFiltered = filtered.filter(
          (item: any) => item.StatusID === 1
        );
        setFilteredActive(activeFiltered);
      }

      const pagination = state.pagination;
      pagination.total = active.length;

      setState({
        ...state,
        loading: false,
        pagination,
        ...params,
      });

      setActive(active);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchInActive = async (params = {}) => {
    try {
      const inactive = products.filter((item: any) => item.StatusID === 2);

      if (filtered.length > 0) {
        const inActiveFiltered = filtered.filter(
          (item: any) => item.StatusID === 2
        );
        setFilteredInActive(inActiveFiltered);
      }

      const pagination = state.pagination;
      pagination.total = inactive.length;

      setState({
        ...state,
        loading: false,
        pagination,
        ...params,
      });
      setInactive(inactive);
    } catch (err) {
      console.log(err);
    }
  };

  const onSearch = async (e: any) => {
    const value = e.target.value;
    if (value !== "") {
      const result = await searchProductsApi(String(groupId), value);
      const productById = new Map(
        products.map((p: any) => [Number(p.id), p])
      );
      // Prefer full local product (Addon, ProductCategory, etc.) over search stubs
      setFiltered(
        result.data
          .filter((p: any) => productById.has(Number(p.id)))
          .map((p: any) => {
            const full = productById.get(Number(p.id));
            return full ? { ...p, ...full } : p;
          })
      );
    } else {
      setFiltered([]);
    }
  };

  // Keep filtered rows in sync when products are updated (e.g. after linking an addon)
  useEffect(() => {
    setFiltered((prev: any[]) => {
      if (!prev.length) return prev;
      const productById = new Map(
        products.map((p: any) => [Number(p.id), p])
      );
      return prev
        .map((fp: any) => productById.get(Number(fp.id)) ?? fp)
        .filter((fp: any) => productById.has(Number(fp.id)));
    });
  }, [products]);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const pager = { ...state.pagination };
    pager.current = pagination.current;
    setState({
      ...state,
      pagination: pager,
    });

    const params = {
      pageSize: pagination.pageSize,
      page: pagination.current,
      sortField: sorter.field,
      sortOrder: sorter.order,
      ...filters,
    };

    fetchActive(params);
  };

  useEffect(() => {
    if (filtered.length === 0) {
      setFilteredActive([]);
      setFilteredInActive([]);
    }
    fetchActive();
    fetchInActive();

    getGroupApi(String(currentGroup))
      .then((res) => {
        if (res.data.GroupLevel === 1) setAuthorized(true);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [products, filtered]);

  const columns = [
    {
      title: "Product Name",
      dataIndex: "ProductName",
      sorter: (a: any, b: any) => a.ProductName.localeCompare(b.ProductName),
    },
    {
      title: "Cover Amount",
      dataIndex: "CoverAmount",
      sorter: (a: any, b: any) => a.CoverAmount - b.CoverAmount,
    },
    authorized && {
      title: "Underwriter Premium",
      dataIndex: "UnderwriterPremium",
    },
    {
      title: "Group Premium",
      dataIndex: "GroupPremium",
    },
    {
      title: "Policy Premium",
      dataIndex: "PolicyPremium",
    },
    {
      title: "Addon/s",
      dataIndex: ["Addon", "Name"],
      render: (_: any, record: any) => (
        <span>
          {record.Addon &&
            record.Addon.map((addon: any) => addon.Name).join(", ")}
        </span>
      ),
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                icon={<EditOutlined style={{ color: "blue" }} />}
                onClick={() => showEditModal(record)}
              >
                Edit Product
              </Menu.Item>
              {state.activeKey === "2" ? (
                <Menu.Item
                  key="activate"
                  icon={<CheckOutlined style={{ color: "orange" }} />}
                  onClick={() => handleDelete(record.id, state.activeKey)}
                >
                  Activate Product
                </Menu.Item>
              ) : (
                <Menu.Item
                  key="remove"
                  icon={<DeleteOutlined style={{ color: "red" }} />}
                  onClick={() => handleDelete(record.id, state.activeKey)}
                >
                  Remove Product
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <MoreOutlined
            rotate={90}
            style={{ cursor: "pointer", fontSize: "16px", color: "#000" }}
          />
        </Dropdown>
      ),
    },
  ].filter(Boolean);

  const tabPanes = [
    {
      key: "1",
      title: "Active",
      content: (
        <DataTable
          persistKey="products"
          dataSource={filteredActive.length > 0 ? filteredActive : active}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: filteredActive.length > 0 ? filteredActive.length : active.length,
    },
    {
      key: "2",
      title: "Inactive",
      content: (
        <DataTable
          persistKey="products"
          dataSource={filteredInActive.length > 0 ? filteredInActive : inactive}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count:
        filteredInActive.length > 0 ? filteredInActive.length : inactive.length,
    },
  ];

  const changeTab = (key: any) => {
    setState({
      ...state,
      activeKey: key,
      loading: false,
    });
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <div className="interactions">
            <Button
              onClick={showModal}
              size="large"
              style={{ marginBottom: "12px" }}
            >
              <PlusOutlined style={{ paddingRight: "5px" }} />
              Add Product
            </Button>

            <div>
              <Search
                placeholder="Search"
                size="large"
                onChange={(e) => onSearch(e)}
                style={{ width: "100%", margin: "24px 0" }}
              />
            </div>
          </div>
          <Tabs
            type="card"
            defaultActiveKey={state.activeKey}
            activeKey={state.activeKey}
            onChange={changeTab}
          >
            {tabPanes.map((pane) => (
              <TabPane
                key={pane.key}
                tab={
                  <>
                    <span>{pane.title}</span>
                    <span
                      style={{
                        color: "#B0B0B0",
                        fontWeight: "300",
                        marginLeft: "3px",
                        fontSize: "12px",
                      }}
                    >
                      ({pane.count})
                    </span>
                  </>
                }
              >
                {pane.content}
              </TabPane>
            ))}
          </Tabs>

          {update ? (
            <EditProductModal
              key={product.id}
              show={show}
              product={product}
              showModal={showModal}
              groupId={groupId}
              handleUpdate={handleUpdate}
            />
          ) : (
            <AddProductModal
              show={show}
              showModal={showModal}
              groupId={groupId}
              handleAdd={handleAdd}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default ProductTable;
